import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { Check, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  bulkUpdateManufacturingProductStatus,
  countManufacturingProducts,
  fetchFamilies,
  fetchSeasons,
} from "@/lib/api/manufacturing";
import { MFG_PRODUCT_STATUS_LABELS } from "@/lib/constants";

export function ProductStatusUtility() {
  const { toast } = useToast();
  const [families, setFamilies] = React.useState([]);
  const [seasons, setSeasons] = React.useState([]);
  const [codFam, setCodFam] = React.useState("");
  const [temporada, setTemporada] = React.useState("");
  const [status, setStatus] = React.useState("obsolete");
  const countRequestRef = React.useRef(0);
  const [count, setCount] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);

  React.useEffect(() => {
    Promise.all([fetchFamilies(), fetchSeasons()]).then(([familiesRes, seasonsRes]) => {
      setFamilies(familiesRes.data || []);
      setSeasons(seasonsRes.data || []);
    });
  }, []);

  const refreshCount = React.useCallback(async () => {
    const request = ++countRequestRef.current;
    if (!codFam && !temporada) {
      setCount(null);
      return;
    }
    setLoading(true);
    setCount(null);
    const result = await countManufacturingProducts({ codFam, temporada });
    if (request !== countRequestRef.current) return;
    setLoading(false);
    if (result.error) {
      toast({ title: "Error", description: "No se pudo contar los artículos", variant: "destructive" });
      return;
    }
    setCount(result.data);
  }, [codFam, temporada, toast]);

  React.useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  const apply = async () => {
    if (!codFam && !temporada) {
      toast({ title: "Validación", description: "Selecciona familia y/o temporada", variant: "destructive" });
      return;
    }
    const label = MFG_PRODUCT_STATUS_LABELS[status] || status;
    if (!window.confirm(`¿Marcar ${count ?? "estos"} artículos como ${label.toLowerCase()}?`)) return;
    setUpdating(true);
    const result = await bulkUpdateManufacturingProductStatus({ codFam, temporada, status });
    setUpdating(false);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo actualizar", variant: "destructive" });
      return;
    }
    toast({ title: "Artículos actualizados", description: `${result.data || 0} artículos → ${label}` });
    await refreshCount();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="rounded-lg border bg-white p-5 shadow-sm">
        <h1 className="text-xl font-semibold">Estado masivo de artículos</h1>
        <p className="mt-1 text-sm text-gray-600">
          Marca como activo u obsoleto todos los artículos de una familia y/o temporada.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <select className="rounded-md border px-3 py-2" value={codFam} onChange={(event) => setCodFam(event.target.value)}>
            <option value="">Todas las familias</option>
            {families.map((family) => (
              <option key={family.cod_fam} value={family.cod_fam.trim()}>
                {family.cod_fam.trim()} · {family.descrip}
              </option>
            ))}
          </select>
          <SeasonSelect data-season-selector="true" className="rounded-md border px-3 py-2" value={temporada} onChange={(event) => setTemporada(event.target.value)}>
            <option value="">Todas las temporadas</option>
            {seasons.map((season) => (
              <option key={season.cod_tem} value={season.cod_tem.trim()}>
                {season.cod_tem.trim()} · {season.descrip}
              </option>
            ))}
          </SeasonSelect>
          <select className="rounded-md border px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="active">Marcar como activo</option>
            <option value="obsolete">Marcar como obsoleto</option>
          </select>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={refreshCount} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Recuento
            </Button>
            <Button type="button" onClick={apply} disabled={updating || loading || count === null || (!codFam && !temporada)}>
              <Check className="mr-2 h-4 w-4" />
              {updating ? "Actualizando..." : "Aplicar"}
            </Button>
          </div>
        </div>
        <p className="mt-3 text-sm text-gray-700">
          {codFam || temporada
            ? loading
              ? "Contando artículos..."
              : `Artículos coincidentes: ${count ?? 0}`
            : "Selecciona al menos un filtro."}
        </p>
      </div>
    </div>
  );
}
